import { useEffect, useState } from 'react'
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuthStore } from './stores/authStore'
import { useLanguageStore } from './stores/languageStore'
import { SupportedLanguage } from '@tvia-ai/shared'
import Navbar from './components/layout/Navbar'
import Footer from './components/layout/Footer'
import LoadingSpinner from './components/ui/LoadingSpinner'
import ProtectedRoute from './components/auth/ProtectedRoute'
import AdminRoute from './components/auth/AdminRoute'
import { CookieConsentBanner } from './components/analytics/CookieConsentBanner'
import TermsApprovalModal from './components/ui/TermsApprovalModal'

// Pages
import HomePage from './pages/HomePage'
import LoginPage from './pages/auth/LoginPage'
import RegisterPage from './pages/auth/RegisterPage'
import EmailVerificationPage from './pages/auth/EmailVerificationPage'
import FileLawsuitPage from './pages/lawsuit/FileLawsuitPage'
import BrowseLawsuitsPage from './pages/lawsuit/BrowseLawsuitsPage'
import LawsuitDetailPage from './pages/lawsuit/LawsuitDetailPage'
import JoinLawsuitPage from './pages/lawsuit/JoinLawsuitPage'
import StrengthenCasePage from './pages/lawsuit/StrengthenCasePage'
import StartSubmissionPage from './pages/lawsuit/StartSubmissionPage'
import EnhancedSubmissionPage from './pages/lawsuit/EnhancedSubmissionPage'
import GenerateDocumentsPage from './pages/lawsuit/GenerateDocumentsPage'
import LawsuitFlowPage from './pages/lawsuit/LawsuitFlowPage'
import PaymentPage from './pages/payment/PaymentPage'
import PaymentSuccessPage from './pages/payment/PaymentSuccessPage'
import DashboardPage from './pages/dashboard/DashboardPage'
import ProfilePage from './pages/profile/ProfilePage'
import AdminDashboardPage from './pages/admin/AdminDashboardPage'
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'
import ContactPage from './pages/ContactPage'
import AboutPage from './pages/AboutPage'
import TermsPage from './pages/TermsPage'
import CookiesPage from './pages/CookiesPage'
import NotFoundPage from './pages/NotFoundPage'
import CareersPage from './pages/CareersPage'
import ClassActionsPage from './pages/ClassActionsPage'
import LegalDocumentsPage from './pages/LegalDocumentsPage'
import PricingPage from './pages/PricingPage'
import HelpPage from './pages/HelpPage'
import FAQPage from './pages/FAQPage'

import BlogPage from './pages/BlogPage'
import PressPage from './pages/PressPage'
import GDPRPage from './pages/GDPRPage'

// Component to handle language detection from URLs
const LanguageRouteWrapper = ({ children }: { children: React.ReactNode }) => {
  const { setLanguage } = useLanguageStore()
  const location = useLocation()
  const navigate = useNavigate()
  const [isProcessing, setIsProcessing] = useState(false)
  
  useEffect(() => {
    // Prevent infinite loops
    if (isProcessing) return
    
    const pathSegments = location.pathname.split('/').filter(Boolean)
    const pathLanguage = pathSegments[0] as SupportedLanguage
    const supportedLanguages: SupportedLanguage[] = ['en', 'he', 'ar', 'de', 'fr']
    
    // Check for URL parameters
    const urlParams = new URLSearchParams(location.search)
    const urlLang = urlParams.get('lang') as SupportedLanguage
    
    // Handle language parameter in path
    if (pathLanguage && supportedLanguages.includes(pathLanguage)) {
      setIsProcessing(true)
      setLanguage(pathLanguage)
      
      // Create new path without language
      pathSegments.shift() // Remove language segment
      const newPath = pathSegments.length > 0 ? '/' + pathSegments.join('/') : '/'
      
      // Use setTimeout to avoid navigation issues
      setTimeout(() => {
        navigate(newPath + location.search, { replace: true })
        setIsProcessing(false)
      }, 0)
      return
    }
    
    // Handle language parameter in query
    if (urlLang && supportedLanguages.includes(urlLang)) {
      setIsProcessing(true)
      setLanguage(urlLang)
      
      // Remove lang parameter from search
      const newParams = new URLSearchParams(location.search)
      newParams.delete('lang')
      const newSearch = newParams.toString() ? '?' + newParams.toString() : ''
      
      setTimeout(() => {
        navigate(location.pathname + newSearch, { replace: true })
        setIsProcessing(false)
      }, 0)
      return
    }
    
    // No language parameters - keep current language
  }, [location.pathname, location.search, setLanguage, navigate, isProcessing])
  
  return <>{children}</>
}


function App() {
  const { user, isLoading, checkAuth, pendingTermsApproval, acceptTerms, logout } = useAuthStore()
  const { language, initializeLanguage, setLanguage, isRTL } = useLanguageStore()
  const [showTermsModal, setShowTermsModal] = useState(false)
  const location = useLocation()

  useEffect(() => {
    // Initialize language first, then authentication - only once on app start
    initializeLanguage()
    checkAuth()
  }, []) // Empty dependency array - run only once

  // Update document direction when language changes
  useEffect(() => {
    const htmlElement = document.documentElement
    if (isRTL) {
      htmlElement.setAttribute('dir', 'rtl')
      htmlElement.setAttribute('lang', language)
    } else {
      htmlElement.setAttribute('dir', 'ltr')
      htmlElement.setAttribute('lang', language)
    }
  }, [language, isRTL])

  useEffect(() => {
    // If user is loaded and has a preferred language, set it (unless already set)
    if (user && user.preferredLanguage && language !== user.preferredLanguage) {
      setLanguage(user.preferredLanguage)
    }
    // Only run when user or language changes
  }, [user, language, setLanguage])

  // Handle terms approval modal
  useEffect(() => {
    // Don't show terms modal if user is on terms or privacy pages
    const isOnTermsOrPrivacyPage = location.pathname === '/terms' || location.pathname === '/privacy'
    
    if (user && pendingTermsApproval && !isOnTermsOrPrivacyPage) {
      setShowTermsModal(true)
    } else {
      setShowTermsModal(false)
    }
  }, [user, pendingTermsApproval, location.pathname])

  const handleTermsAccept = async () => {
    try {
      await acceptTerms()
      setShowTermsModal(false)
    } catch (error) {
      // Error handling is done in the store
    }
  }

  const handleTermsCancel = async () => {
    try {
      await logout()
      setShowTermsModal(false)
    } catch (error) {
      // Error handling is done in the store
    }
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className={`min-h-screen bg-gray-50 ${isRTL ? 'rtl' : 'ltr'}`}>
      <Navbar />
      
      <main className="flex-1">
        <LanguageRouteWrapper>
          <Routes>
            {/* Main routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <LoginPage />} />
            <Route path="/register" element={user ? <Navigate to="/dashboard" /> : <RegisterPage />} />
            <Route path="/verify-email" element={<EmailVerificationPage />} />
            <Route path="/careers" element={<CareersPage />} />
            
            {/* Static pages */}
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/cookies" element={<CookiesPage />} />
            <Route path="/class-actions" element={<ClassActionsPage />} />
            <Route path="/legal-documents" element={<LegalDocumentsPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/faq" element={<FAQPage />} />

            <Route path="/blog" element={<BlogPage />} />
            <Route path="/press" element={<PressPage />} />
            <Route path="/gdpr" element={<GDPRPage />} />
          
            {/* Protected routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            } />
            
            <Route path="/profile" element={
              <ProtectedRoute>
                <ProfilePage />
              </ProtectedRoute>
            } />
            
            <Route path="/file-lawsuit" element={
              <ProtectedRoute>
                <FileLawsuitPage />
              </ProtectedRoute>
            } />
            
            <Route path="/lawsuit-flow" element={<LawsuitFlowPage />} />
            
            <Route path="/lawsuit-wizard" element={
              <ProtectedRoute>
                <FileLawsuitPage />
              </ProtectedRoute>
            } />
            
            <Route path="/browse-lawsuits" element={
              <ProtectedRoute>
                <BrowseLawsuitsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/lawsuit/:id" element={
              <ProtectedRoute>
                <LawsuitDetailPage />
              </ProtectedRoute>
            } />
            
            <Route path="/join-lawsuit/:id" element={
              <ProtectedRoute>
                <JoinLawsuitPage />
              </ProtectedRoute>
            } />
            
            <Route path="/lawsuit/:id/strengthen" element={
              <ProtectedRoute>
                <StrengthenCasePage />
              </ProtectedRoute>
            } />
            
            <Route path="/lawsuit/:id/submission" element={
              <ProtectedRoute>
                <EnhancedSubmissionPage />
              </ProtectedRoute>
            } />
            
            <Route path="/lawsuit/:id/submission/legacy" element={
              <ProtectedRoute>
                <StartSubmissionPage />
              </ProtectedRoute>
            } />
            
            <Route path="/lawsuit/:id/generate-documents" element={
              <ProtectedRoute>
                <GenerateDocumentsPage />
              </ProtectedRoute>
            } />
            
            <Route path="/payment/:lawsuitId" element={
              <ProtectedRoute>
                <PaymentPage />
              </ProtectedRoute>
            } />
            
            <Route path="/payment-success" element={
              <ProtectedRoute>
                <PaymentSuccessPage />
              </ProtectedRoute>
            } />
            
            {/* Admin routes */}
            <Route path="/admin/*" element={
              <AdminRoute>
                <AdminDashboardPage />
              </AdminRoute>
            } />
            
            {/* Localized routes */}
            {/* Hebrew routes */}
            <Route path="/הגשת-תביעה" element={
              <ProtectedRoute>
                <FileLawsuitPage />
              </ProtectedRoute>
            } />
            
            <Route path="/עיון-תביעות" element={
              <ProtectedRoute>
                <BrowseLawsuitsPage />
              </ProtectedRoute>
            } />
            
            {/* Arabic routes */}
            <Route path="/رفع-دعوى" element={
              <ProtectedRoute>
                <FileLawsuitPage />
              </ProtectedRoute>
            } />
            
            <Route path="/تصفح-الدعاوى" element={
              <ProtectedRoute>
                <BrowseLawsuitsPage />
              </ProtectedRoute>
            } />
            
            {/* German routes */}
            <Route path="/klage-einreichen" element={
              <ProtectedRoute>
                <FileLawsuitPage />
              </ProtectedRoute>
            } />
            
            <Route path="/klagen-durchsuchen" element={
              <ProtectedRoute>
                <BrowseLawsuitsPage />
              </ProtectedRoute>
            } />
            
            {/* French routes */}
            <Route path="/deposer-plainte" element={
              <ProtectedRoute>
                <FileLawsuitPage />
              </ProtectedRoute>
            } />
            
            <Route path="/parcourir-plaintes" element={
              <ProtectedRoute>
                <BrowseLawsuitsPage />
              </ProtectedRoute>
            } />
            
            {/* 404 page */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </LanguageRouteWrapper>
      </main>
      
      <Footer />
      <CookieConsentBanner />
      
      {/* Global Terms Approval Modal */}
      <TermsApprovalModal
        isOpen={showTermsModal}
        onClose={handleTermsCancel} // Logout user when terms are cancelled
        onAccept={handleTermsAccept}
        isLoading={isLoading}
      />
    </div>
  )
}

export default App 